import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/20/solid';
import { Popover } from '@headlessui/react';
import { HexColorPicker } from 'react-colorful';

import { useAuth } from '../../hooks/use-auth';
import CommonButtonDefault from '../../components/common/buttons/default';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import * as badgesService from '../../services/api/badges';
import ValidationError from '../../entities/validation-error';
import LabelWithError from '../../components/common/form/label-with-error';
import StyledInput from '../../components/common/form/styled-input';
import { strings } from '../../localization/strings';
import CommonConfirmationModal from '../../components/modals/confirmation';
import StyledSwitch from '../../components/common/form/styled-switch';
import Heading1 from '../../components/common/headings/heading1';
import BadgeForm from '../../entities/form/badge';
import { BadgeTranslation } from '../../entities/badge-translation';
import { Language } from '../../entities/language';
import * as languagesService from '../../services/api/languages';

interface ComponentProps {
  isEdit?: boolean;
}

const normalizeTranslations = (languages: Language[], translations?: BadgeTranslation[]): BadgeTranslation[] => {
  return languages.map((language) => {
    const existingTranslation = translations?.find((item) => item.language_id === language.id);

    return {
      language_id: language.id,
      title: '',
      comment: null,
      is_active: 0,
      ...existingTranslation,
    };
  });
};

const cardClassName = 'rounded-lg bg-white ring-1 ring-gray-900/5';

function BadgesEditPageComponent({ isEdit }: ComponentProps) {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const {
    register,
    formState: { errors },
    clearErrors,
    setError,
    control,
    reset,
    setValue,
    handleSubmit,
  } = useForm<BadgeForm>({
    defaultValues: {
      color: '#3574D1',
      private: 0,
      change_renewal_date: 0,
      translations: [],
    },
  });
  const translations = useWatch({ control, name: 'translations' });

  const onDelete = async () => {
    if (id) {
      setIsLoading(true);
      await badgesService.remove(token, parseInt(id, 10));

      navigate('/badges');
    }
  };

  const onSubmit = async (data: BadgeForm) => {
    setIsLoading(true);
    clearErrors();

    try {
      const formattedData: BadgeForm = {
        ...data,
        translations: data.translations.map((translation) => ({
          ...translation,
          comment: translation.comment || null,
        })),
      };

      if (isEdit && id) {
        await badgesService.update(token, parseInt(id, 10), formattedData);
      } else {
        await badgesService.create(token, formattedData);
      }

      navigate('/badges?success=1');
    } catch (e) {
      setIsLoading(false);

      const err = e as ValidationError<BadgeForm>;

      err.errors.forEach((item) => {
        setError(item.key as never, { message: item.value });
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const availableLanguages = await languagesService.list(token);

        setLanguages(availableLanguages || []);

        if (isEdit && id) {
          const item = await badgesService.one(token, id);

          reset({
            color: item.color,
            private: item.private,
            change_renewal_date: item.change_renewal_date,
            translations: normalizeTranslations(availableLanguages || [], item.translations),
          });
        } else {
          setValue('translations', normalizeTranslations(availableLanguages || []));
        }

        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit, reset, setValue, token]);

  return (
    <div>
      {isLoading && <CommonAnimatedLoader />}
      {!isLoading && (
        <>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <div>
                  <a href="/" className="text-gray-400 hover:text-gray-500">
                    <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <span className="sr-only">{strings.common.home}</span>
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <a href="/badges" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                    {strings.navigation.badges}
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="ml-4 text-sm font-medium text-gray-500" aria-current="page">
                    {isEdit ? strings.badges.editTitle : strings.badges.createTitle}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
          <header className="mb-3 mt-3">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Heading1>{isEdit ? strings.badges.editTitle : strings.badges.createTitle}</Heading1>
            </div>
          </header>
          <main>
            <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-6">
                  <section className={cardClassName}>
                    <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
                      <h2 className="text-base font-semibold leading-6 text-gray-900">
                        {strings.badges.form.sharedSettings}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-6 p-4 sm:grid-cols-12">
                      <div className="sm:col-span-4">
                        <LabelWithError title={strings.badges.form.color} error={errors.color?.message} hasStar>
                          <Controller
                            name="color"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <Popover className="relative">
                                <div className="flex">
                                  <Popover.Button
                                    type="button"
                                    aria-label={strings.badges.form.openColorPicker}
                                    className="flex w-12 items-center justify-center rounded-md border border-gray-300 rounded-r-none border-r-0 bg-white"
                                  >
                                    <span
                                      className="h-6 w-6 rounded border border-gray-300"
                                      style={{ backgroundColor: field.value || '#3574D1' }}
                                    />
                                  </Popover.Button>
                                  <StyledInput
                                    type="text"
                                    name={field.name}
                                    value={field.value || ''}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                    ref={field.ref}
                                    noValidation
                                    withPreItem
                                    hasError={!!errors.color}
                                  />
                                </div>
                                <Popover.Panel className="absolute left-0 top-12 z-10 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                                  <HexColorPicker
                                    color={field.value || '#3574D1'}
                                    onChange={field.onChange}
                                    aria-label={strings.badges.form.color}
                                  />
                                </Popover.Panel>
                              </Popover>
                            )}
                          />
                        </LabelWithError>
                      </div>
                      <div className="sm:col-span-4">
                        <div className="relative mt-7 flex items-start py-1">
                          <div className="flex h-6 items-center">
                            <StyledSwitch name="change_renewal_date" control={control} />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label htmlFor="change_renewal_date" className="font-medium text-gray-900">
                              {strings.badges.form.changeRenewalDate}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="sm:col-span-4">
                        <div className="relative mt-7 flex items-start py-1">
                          <div className="flex h-6 items-center">
                            <StyledSwitch name="private" control={control} />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label htmlFor="private" className="font-medium text-gray-900">
                              {strings.badges.form.private}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h2 className="px-1 text-base font-semibold text-gray-900">{strings.badges.form.translations}</h2>
                    <div className="mt-6 space-y-6">
                      {languages.map((language, index) => (
                        <section key={language.id} className={cardClassName}>
                          <div className="flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
                            <h3 className="text-base font-semibold leading-6 text-gray-900">{language.name}</h3>
                            <div className="flex items-center gap-3">
                              <label htmlFor={`translations.${index}.is_active`} className="text-sm text-gray-700">
                                {strings.badges.form.active}
                              </label>
                              <StyledSwitch name={`translations.${index}.is_active`} control={control} />
                            </div>
                          </div>
                          <input
                            type="hidden"
                            {...register(`translations.${index}.language_id` as const, { valueAsNumber: true })}
                          />
                          {!!translations?.[index]?.is_active && (
                            <div className="grid grid-cols-1 gap-6 p-4 sm:grid-cols-2">
                              <div>
                                <LabelWithError
                                  title={strings.badges.form.title}
                                  error={errors.translations?.[index]?.title?.message}
                                >
                                  <StyledInput
                                    type="text"
                                    {...register(`translations.${index}.title` as const)}
                                    hasError={!!errors.translations?.[index]?.title}
                                  />
                                </LabelWithError>
                              </div>
                              <div>
                                <LabelWithError
                                  title={strings.badges.form.comment}
                                  error={errors.translations?.[index]?.comment?.message}
                                >
                                  <textarea
                                    {...register(`translations.${index}.comment` as const)}
                                    rows={3}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  />
                                </LabelWithError>
                              </div>
                            </div>
                          )}
                        </section>
                      ))}
                    </div>
                  </section>

                  <div className="flex justify-end space-x-4 px-1 pb-2 pt-2">
                    {isEdit && (
                      <CommonButtonDefault onClick={() => setConfirmDelete(true)} type="button" danger>
                        {strings.button.delete}
                      </CommonButtonDefault>
                    )}
                    <CommonButtonDefault onClick={() => navigate('/badges')} type="button" secondary>
                      {strings.button.cancel}
                    </CommonButtonDefault>
                    <CommonButtonDefault type="submit" primary>
                      {strings.button.save}
                    </CommonButtonDefault>
                  </div>
                </div>
              </form>
            </div>
          </main>

          <CommonConfirmationModal
            open={confirmDelete}
            cancelled={() => setConfirmDelete(false)}
            confirmed={() => onDelete()}
            danger
            title={strings.common.confirmation.deleteBadge.title}
            description={strings.common.confirmation.deleteBadge.description}
            buttonConfirm={strings.button.delete}
            buttonCancel={strings.button.cancel}
          />
        </>
      )}
    </div>
  );
}

BadgesEditPageComponent.defaultProps = {
  isEdit: false,
};

export default BadgesEditPageComponent;
